/**
 * Client-side CSV Export Utility
 * Generates CSV files in the browser - no server costs!
 */

/**
 * Convert CEU data to CSV format
 * @param {Array} ceus - Array of CEU records
 * @param {Object} userProfile - User profile data
 * @returns {string} CSV content
 */
export function generateCEUCSV(ceus, userProfile) {
  const certifyingBody = userProfile?.certifyingBody || 'RID';
  
  // Calculate totals by category
  const professionalStudies = ceus
    .filter(c => c.category === 'Professional Studies')
    .reduce((sum, c) => sum + (parseFloat(c.ceuHours) || 0), 0);
  
  const generalStudies = ceus
    .filter(c => c.category === 'General Studies')
    .reduce((sum, c) => sum + (parseFloat(c.ceuHours) || 0), 0);
  
  const ppo = ceus
    .filter(c => c.subCategory?.includes('PPO'))
    .reduce((sum, c) => sum + (parseFloat(c.ceuHours) || 0), 0);
  
  const totalCEUs = ceus.reduce((sum, ceu) => sum + (parseFloat(ceu.ceuHours) || 0), 0);

  // Requirements based on certifying body
  const reqTotal = certifyingBody === 'BEI' ? 4.0 : 8.0;
  const reqProfessional = certifyingBody === 'BEI' ? 3.0 : 6.0;
  const reqGeneral = certifyingBody === 'BEI' ? 1.0 : 2.0;
  const reqPPO = certifyingBody === 'BEI' ? 0.5 : 1.0;

  // User info header
  const userInfo = [
    `CEU AUDIT REPORT - ${certifyingBody}`,
    `Name: ${userProfile?.firstName || ''} ${userProfile?.lastName || ''}`,
    `Export Date: ${new Date().toLocaleDateString()}`,
    ``,
    `CEU REQUIREMENTS SUMMARY`,
    `Category,Required,Earned,Remaining`,
    `Professional Studies,${reqProfessional.toFixed(1)},${professionalStudies.toFixed(1)},${Math.max(0, reqProfessional - professionalStudies).toFixed(1)}`,
    `General Studies,${reqGeneral.toFixed(1)},${generalStudies.toFixed(1)},${Math.max(0, reqGeneral - generalStudies).toFixed(1)}`,
    `PPO Activities,${reqPPO.toFixed(1)},${ppo.toFixed(1)},${Math.max(0, reqPPO - ppo).toFixed(1)}`,
    `TOTAL,${reqTotal.toFixed(1)},${totalCEUs.toFixed(1)},${Math.max(0, reqTotal - totalCEUs).toFixed(1)}`,
    ``,
    `WORKSHOP DETAILS`,
  ];

  // Audit-relevant headers only
  const headers = [
    'Date',
    'Workshop Title',
    'Instructor',
    'Organization',
    'CEUs Granted'
  ];

  const rows = ceus.map(ceu => [
    ceu.date || '',
    `"${(ceu.title || '').replace(/"/g, '""')}"`,
    `"${(ceu.instructor || '').replace(/"/g, '""')}"`,
    `"${(ceu.organization || ceu.provider || '').replace(/"/g, '""')}"`,
    ceu.ceuHours || 0
  ]);

  return [...userInfo, headers.join(','), ...rows.map(row => row.join(','))].join('\n');
}

/**
 * Download CSV file (client-side only - no server costs)
 * @param {string} csvContent - CSV content string
 * @param {string} filename - Filename for download
 */
export function downloadCSV(csvContent, filename) {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

/**
 * Export user's CEU data as CSV (completely client-side)
 * @param {Array} ceus - CEU records
 * @param {Object} userProfile - User profile
 */
export function exportCEUsAsCSV(ceus, userProfile) {
  const csvContent = generateCEUCSV(ceus, userProfile);
  const filename = `ceu-records-${userProfile?.firstName || 'user'}-${new Date().toISOString().split('T')[0]}.csv`;
  downloadCSV(csvContent, filename);
}

/**
 * Cost-effective export - no server processing needed!
 * Benefits:
 * - Zero server costs
 * - Instant download
 * - Works offline
 * - No data transmission to server
 * - Secure (data stays in browser)
 */
